import React, {useState, useEffect, useRef} from 'react';
import {callOpenRouterApi} from '../../services/openrouterApi';
import {UserProfile, ChatMessage as ChatMessageType} from '../../types';
import {getIDDGuidePrompt} from '../../utils/prompts';
import {supabase} from '../../services/supabase';
import ChatMessage from '../common/ChatMessage';
import InputField from '../common/InputField';
import '../../styles/components/IDDGuide.css';
import {validateLaTeX} from '../../utils/mathUtils';
import {parseStructuredResponse, TableData} from '../../utils/responseFormatter';

interface ChatSession {
  id: string;
  title: string;
  timestamp: Date;
  messages: ChatMessageType[];
}

interface StudentProfile {
  name: string;
  age: string;
  grade: string;
  diagnosis: string;
  communicationMode: string;
  readingLevel: string;
  mathLevel: string;
  strengths: string;
  challenges: string;
  interests: string;
}

interface IDDGuideProps {
  userProfile: UserProfile;
  sessionId?: string;
}

const IDDGuide: React.FC<IDDGuideProps> = ({userProfile, sessionId}) => {
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<string>('');
  const [messages, setMessages] = useState<ChatMessageType[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showStudentForm, setShowStudentForm] = useState(true);
  const [studentProfile, setStudentProfile] = useState<StudentProfile>({
    name: '',
    age: '',
    grade: '',
    diagnosis: '', // Optional, defaults to unknown
    communicationMode: '',
    readingLevel: '',
    mathLevel: '',
    strengths: '',
    challenges: '',
    interests: '',
  });
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const [searchQuery, setSearchQuery] = useState('');
  const [theme, setTheme] = useState<'light' | 'dark'>('dark'); // Default to dark theme
  const [errorMessage, setErrorMessage] = useState('');

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const sidebarToggleRef = useRef<HTMLButtonElement>(null);

  // Auto-scroll to latest message
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({behavior: 'smooth'});
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, showStudentForm]);

  // Load existing sessions from Supabase on mount
  useEffect(() => {
    const loadSessions = async () => {
      try {
        const {data: {user}} = await supabase.auth.getUser();
        if (!user) return;

        const {data: sessionsData, error: sessionsError} = await supabase
          .from('chat_sessions')
          .select('*')
          .eq('user_id', user.id)
          .eq('chat_type', 'idd-guide')
          .order('created_at', {ascending: false});

        if (sessionsError) throw sessionsError;

        if (sessionsData && sessionsData.length > 0) {
          const loadedSessions: ChatSession[] = await Promise.all(
            sessionsData.map(async (session) => {
              const {data: messagesData, error: messagesError} = await supabase
                .from('chat_messages')
                .select('*')
                .eq('session_id', session.id)
                .order('created_at', {ascending: true});

              if (messagesError) throw messagesError;

              const messages: ChatMessageType[] = (messagesData || []).map((msg) => ({
                id: msg.id,
                role: msg.role,
                content: msg.content,
                timestamp: new Date(msg.created_at),
                chat_type: 'idd-guide',
              }));

              return {
                id: session.id,
                title: session.title,
                timestamp: new Date(session.created_at),
                messages,
              };
            })
          );

          setSessions(loadedSessions);
        }
      } catch (error) {
        console.error('Error loading sessions:', error);
        setErrorMessage('Error loading chat history.');
      }
    };

    loadSessions();
  }, []);

  // Generate chat title from first message
  const generateChatTitle = (firstMessage: string): string => {
    const words = firstMessage.trim().split(' ').slice(0, 6);
    return words.join(' ') + (words.length < firstMessage.split(' ').length ? '...' : '');
  };

  // Save chat message to database
  const saveChatMessage = async (role: 'user' | 'assistant', content: string) => {
    if (!currentSessionId) {
      console.error('No session ID available for saving message');
      return;
    }

    try {
      const {error} = await supabase
        .from('chat_messages')
        .insert({
          session_id: currentSessionId,
          role,
          content,
          created_at: new Date().toISOString(),
        });

      if (error) throw error;
    } catch (error) {
      console.error('Error saving message:', error);
      setErrorMessage('Error saving message. Please try again.');
    }
  };

  // Handle student profile changes
  const handleStudentProfileChange = (field: keyof StudentProfile, value: string) => {
    setStudentProfile((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle student profile submission
  const handleSubmitStudentProfile = async () => {
    // Only require name and age, diagnosis is optional
    if (!studentProfile.name.trim() || !studentProfile.age.trim()) {
      setErrorMessage('Please fill in at least name and age fields.');
      return;
    }
    setErrorMessage('');
    setShowStudentForm(false);

    // Create session in Supabase first
    try {
      const {data: {user}} = await supabase.auth.getUser();
      if (!user) {
        setErrorMessage('User not authenticated');
        return;
      }

      const sessionTitle = `IDD Guide for ${studentProfile.name}`;
      
      // Insert and get the returned session ID
      const {data: newSession, error: sessionError} = await supabase
        .from('chat_sessions')
        .insert({
          user_id: user.id,
          title: sessionTitle,
          chat_type: 'idd-guide',
        })
        .select()
        .single();

      if (sessionError) throw sessionError;
      if (!newSession) throw new Error('Failed to create session');

      // Use the database-generated session ID
      const dbSessionId = newSession.id;
      setCurrentSessionId(dbSessionId);

      const welcomeMessage: ChatMessageType = {
        id: Date.now().toString(),
        role: 'assistant',
        content: `Thank you for providing information about ${studentProfile.name}. I'm ready to help you create individualized lessons and curriculum. What would you like to focus on today?`,
        timestamp: new Date(),
        chat_type: 'idd-guide',
      };

      setMessages([welcomeMessage]);

      // Save welcome message to database
      const {error: messageError} = await supabase
        .from('chat_messages')
        .insert({
          session_id: dbSessionId,
          role: 'assistant',
          content: welcomeMessage.content,
          created_at: new Date().toISOString(),
        });

      if (messageError) throw messageError;

      // Update local sessions state
      setSessions((prev) => [
        {
          id: dbSessionId,
          title: sessionTitle,
          timestamp: new Date(),
          messages: [welcomeMessage],
        },
        ...prev,
      ]);
    } catch (error) {
      console.error('Error creating chat session:', error);
      setErrorMessage('Error creating chat session. Please try again.');
    }
  };

  // Handle editing student profile
  const handleEditStudentProfile = () => {
    setShowStudentForm(true);
  };

  // Handle message submission
  const handleSubmit = async () => {
    if (!input.trim() || isLoading) return;

    setIsLoading(true);
    setErrorMessage('');

    const userMessage: ChatMessageType = {
      id: Date.now().toString(),
      role: 'user',
      content: input,
      timestamp: new Date(),
      chat_type: 'idd-guide',
    };

    const newMessages = [...messages, userMessage];
    setMessages(newMessages);

    // Update session title if it's the first user message
    if (messages.length === 1) {
      const title = generateChatTitle(input);
      
      // Update in database
      try {
        await supabase
          .from('chat_sessions')
          .update({title})
          .eq('id', currentSessionId);
      } catch (error) {
        console.error('Error updating session title:', error);
      }

      // Update local state
      setSessions((prev) =>
        prev.map((session) =>
          session.id === currentSessionId ? {...session, title, messages: newMessages} : session
        )
      );
    }

    await saveChatMessage('user', input);

    try {
      const fullPrompt = getIDDGuidePrompt(userProfile, input, studentProfile);

      const response = await callOpenRouterApi(fullPrompt, userProfile);
      const finalResponse = validateLaTeX(response);

      const assistantMessage: ChatMessageType = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: finalResponse,
        timestamp: new Date(),
        chat_type: 'idd-guide',
      };

      setMessages((prev) => [...prev, assistantMessage]);
      await saveChatMessage('assistant', finalResponse);

      // Update session messages in local state
      setSessions((prev) =>
        prev.map((session) =>
          session.id === currentSessionId
            ? {...session, messages: [...session.messages, userMessage, assistantMessage]}
            : session
        )
      );
    } catch (error) {
      console.error('Error:', error);
      setErrorMessage('Sorry, I encountered an error while processing your request. Please try again in a moment. If this persists, check your API key configuration.');
    } finally {
      setInput('');
      setIsLoading(false);
    }
  };

  // Create new chat session
  const createNewChat = () => {
    setCurrentSessionId('');
    setMessages([]);
    setShowStudentForm(true);
    setStudentProfile({
      name: '',
      age: '',
      grade: '',
      diagnosis: '', // Optional, defaults to unknown
      communicationMode: '',
      readingLevel: '',
      mathLevel: '',
      strengths: '',
      challenges: '',
      interests: '',
    });
    setErrorMessage('');
  };

  // Switch chat session
  const switchToSession = (sessionId: string) => {
    const session = sessions.find((s) => s.id === sessionId);
    if (session) {
      setCurrentSessionId(sessionId);
      setMessages(session.messages);
      setShowStudentForm(false);
      setErrorMessage('');
    }
  };

  // Delete chat session
  const deleteSession = async (sessionId: string) => {
    try {
      // Delete from database
      const {error} = await supabase
        .from('chat_sessions')
        .delete()
        .eq('id', sessionId);

      if (error) throw error;

      // Update local state
      setSessions((prev) => prev.filter((s) => s.id !== sessionId));
      
      if (currentSessionId === sessionId) {
        const remainingSessions = sessions.filter((s) => s.id !== sessionId);
        if (remainingSessions.length > 0) {
          switchToSession(remainingSessions[0].id);
        } else {
          createNewChat();
        }
      }
    } catch (error) {
      console.error('Error deleting session:', error);
      setErrorMessage('Error deleting session. Please try again.');
    }
  };

  // Filter sessions based on search
  const filteredSessions = sessions.filter((session) =>
    session.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Enhanced format response using responseFormatter
  const renderTableComponent = (table: TableData, index: number) => {
    return (
      <div key={index} style={{overflowX: 'auto', margin: '20px 0'}}>
        <table className="response-table">
          <thead>
            <tr>
              {table.headers.map((header, idx) => (
                <th key={idx}>{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row, rowIdx) => (
              <tr key={rowIdx}>
                {row.map((cell, cellIdx) => (
                  <td key={cellIdx}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const formatResponse = (content: string) => {
    const parsed = parseStructuredResponse(content);
    
    if (parsed.sections.length === 0) {
      // Fallback to basic formatting if no sections found
      return <div className="formatted-response">{formatTextContent(content)}</div>;
    }

    return (
      <div className="formatted-response">
        {parsed.sections.map((section, idx) => (
          <div key={idx} className="response-section">
            <div className="section-header">
              <span className="section-emoji">{section.emoji}</span>
              <h3 className="section-title">{section.title}</h3>
            </div>
            <div className="section-content">
              {section.content && (
                <div className="section-text">{formatTextContent(section.content)}</div>
              )}
              {section.tables.map((table, tableIdx) => renderTableComponent(table, tableIdx))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const formatTextContent = (text: string) => {
    if (!text) return null;
    
    // Handle bullet points
    if (text.includes('- ') || text.includes('• ')) {
      const lines = text.split('\n');
      const items: string[] = [];
      let currentText = '';
      
      lines.forEach(line => {
        if (line.trim().startsWith('-') || line.trim().startsWith('•')) {
          if (currentText) {
            items.push(currentText);
            currentText = '';
          }
          items.push(line.trim().substring(1).trim());
        } else if (line.trim()) {
          if (currentText) {
            currentText += ' ' + line.trim();
          } else {
            currentText = line.trim();
          }
        }
      });
      
      if (currentText) items.push(currentText);
      
      if (items.length > 0) {
        return (
          <ul className="response-list">
            {items.map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </ul>
        );
      }
    }
    
    // Handle line breaks and paragraphs
    const paragraphs = text.split('\n').filter(p => p.trim());
    return paragraphs.map((para, idx) => (
      <p key={idx} className="response-paragraph">{para}</p>
    ));
  };

  return (
    <div className={`idd-guide-wrapper ${theme}`}>
      <div className="background-container">
        {theme === 'light' ? (
          <div className="morning-sky"></div>
        ) : (
          <div className="night-sky"></div>
        )}
      </div>
      
      {errorMessage && (
        <div className="error-message">
          {errorMessage}
        </div>
      )}
      
      <div className="app-container">
        {/* Sidebar */}
        <div className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
          <div className="sidebar-header">
            <div className="logo">🌟</div>
            {sidebarOpen && <span className="app-name">IDD Guide</span>}
          </div>

          {/* Theme Toggle inside sidebar when open */}
          {sidebarOpen && (
            <div className="theme-toggle-sidebar">
              <button onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')} className="theme-toggle-btn">
                {theme === 'dark' ? '☀️ Light Mode' : '🌙 Dark Mode'}
              </button>
            </div>
          )}
          
          {sidebarOpen && (
            <>
              <button className="new-chat-btn" onClick={createNewChat}>
                ➕ New Chat
              </button>
              <div className="search-container">
                <input
                  type="text"
                  placeholder="Search chats..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input"
                />
              </div>
              <div className="chat-history">
                {filteredSessions.map((session) => (
                  <div
                    key={session.id}
                    className={`chat-session-item ${currentSessionId === session.id ? 'active' : ''}`}
                    onClick={() => switchToSession(session.id)}
                  >
                    <div className="session-title">{session.title}</div>
                    <div className="session-info">
                      <span className="session-timestamp">
                        {session.timestamp.toLocaleDateString()}
                      </span>
                    </div>
                    <button
                      className="delete-session-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteSession(session.id);
                      }}
                    >
                      🗑️
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Main Content */}
        <div className="main-content">
          {/* Sidebar Toggle Button - Positioned at top left when sidebar is closed, inside when open */}
          <button
            ref={sidebarToggleRef}
            className={`sidebar-toggle ${sidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label={sidebarOpen ? 'Close sidebar' : 'Open sidebar'}
          >
            {sidebarOpen ? '◀' : '▶'}
          </button>

          {/* Header */}
          <div className="chatbot-header">
            <div className="header-content">
              <div className="header-title-container">
                <h1 className="chatbot-title">IDD GUIDE</h1>
                <p className="chatbot-tagline">
                  <em>Personalized learning plans for students with intellectual and developmental disabilities</em>
                </p>
              </div>
              {!showStudentForm && (
                <div className="student-profile-header">
                  <div className="profile-summary">
                    <h3>Student: {studentProfile.name}</h3>
                    <p>{studentProfile.age} • {studentProfile.grade || 'Grade not specified'} • {studentProfile.diagnosis || 'Diagnosis unknown'}</p>
                    <div className="profile-tags">
                      <span className="tag">Comm: {studentProfile.communicationMode || 'Not specified'}</span>
                      <span className="tag">Reading: {studentProfile.readingLevel || 'Not specified'}</span>
                      <span className="tag">Math: {studentProfile.mathLevel || 'Not specified'}</span>
                    </div>
                  </div>
                  <button onClick={handleEditStudentProfile} className="edit-profile-button">
                    Edit Profile
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Chat Messages or Student Profile Form */}
          <div className="chat-messages-container" ref={chatContainerRef}>
            {showStudentForm ? (
              <div className="student-profile-form">
                <h2>Student Profile Information</h2>
                <p className="form-subtitle">Please provide information about the student to create individualized lessons</p>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Student Name *</label>
                    <input
                      type="text"
                      value={studentProfile.name}
                      onChange={(e) => handleStudentProfileChange('name', e.target.value)}
                      placeholder="Enter student's name"
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label>Age *</label>
                    <input
                      type="text"
                      value={studentProfile.age}
                      onChange={(e) => handleStudentProfileChange('age', e.target.value)}
                      placeholder="e.g., 12 years"
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label>Grade Level</label>
                    <input
                      type="text"
                      value={studentProfile.grade}
                      onChange={(e) => handleStudentProfileChange('grade', e.target.value)}
                      placeholder="e.g., 5th grade"
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label>Diagnosis/Condition (Optional)</label>
                    <input
                      type="text"
                      value={studentProfile.diagnosis}
                      onChange={(e) => handleStudentProfileChange('diagnosis', e.target.value)}
                      placeholder="e.g., Autism Spectrum Disorder, Down Syndrome (optional)"
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label>Communication Mode</label>
                    <select
                      value={studentProfile.communicationMode}
                      onChange={(e) => handleStudentProfileChange('communicationMode', e.target.value)}
                      className="form-select"
                    >
                      <option value="">Select communication mode</option>
                      <option value="Verbal">Verbal</option>
                      <option value="Non-verbal">Non-verbal</option>
                      <option value="AAC Device">AAC Device</option>
                      <option value="Sign Language">Sign Language</option>
                      <option value="PECS">PECS (Picture Exchange)</option>
                      <option value="Mixed Modality">Mixed Modality</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Reading Level</label>
                    <select
                      value={studentProfile.readingLevel}
                      onChange={(e) => handleStudentProfileChange('readingLevel', e.target.value)}
                      className="form-select"
                    >
                      <option value="">Select reading level</option>
                      <option value="Pre-reader">Pre-reader</option>
                      <option value="Emergent Reader">Emergent Reader</option>
                      <option value="Early Reader">Early Reader</option>
                      <option value="Developing Reader">Developing Reader</option>
                      <option value="Fluent Reader">Fluent Reader</option>
                      <option value="Advanced Reader">Advanced Reader</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Math Level</label>
                    <select
                      value={studentProfile.mathLevel}
                      onChange={(e) => handleStudentProfileChange('mathLevel', e.target.value)}
                      className="form-select"
                    >
                      <option value="">Select math level</option>
                      <option value="Pre-math">Pre-math skills</option>
                      <option value="Number Recognition">Number Recognition</option>
                      <option value="Basic Counting">Basic Counting</option>
                      <option value="Simple Operations">Simple Operations (+-)</option>
                      <option value="Intermediate Math">Intermediate Math</option>
                      <option value="Advanced Math">Advanced Math</option>
                    </select>
                  </div>
                  <div className="form-group full-width">
                    <label>Strengths & Abilities</label>
                    <textarea
                      value={studentProfile.strengths}
                      onChange={(e) => handleStudentProfileChange('strengths', e.target.value)}
                      placeholder="What does the student do well? Special talents? Learning strengths?"
                      rows={3}
                      className="form-textarea"
                    />
                  </div>
                  <div className="form-group full-width">
                    <label>Challenges & Support Needs</label>
                    <textarea
                      value={studentProfile.challenges}
                      onChange={(e) => handleStudentProfileChange('challenges', e.target.value)}
                      placeholder="What areas need support? Behavioral challenges? Learning barriers?"
                      rows={3}
                      className="form-textarea"
                    />
                  </div>
                  <div className="form-group full-width">
                    <label>Interests & Motivators</label>
                    <textarea
                      value={studentProfile.interests}
                      onChange={(e) => handleStudentProfileChange('interests', e.target.value)}
                      placeholder="What does the student enjoy? Special interests? Motivational activities?"
                      rows={3}
                      className="form-textarea"
                    />
                  </div>
                </div>
                <div className="form-actions">
                  <button onClick={handleSubmitStudentProfile} className="submit-button">
                    Save Profile & Continue
                  </button>
                </div>
              </div>
            ) : (
              <>
                {messages.length === 0 ? (
                  <div className="welcome-message">
                    <div className="welcome-icon">🌟✨</div>
                    <h3>Welcome to IDD Guide!</h3>
                    <p>I'm here to help you create individualized lessons and curriculum for {studentProfile.name}.</p>
                    <p>What would you like to work on for {studentProfile.name} today?</p>
                  </div>
                ) : (
                  messages.map((message) => (
                    <div key={message.id} className="message-wrapper">
                      {message.role === 'assistant' ? (
                        <div className="formatted-chat-message">
                          <div className="message-role">🤖 Assistant</div>
                          <div className="message-content">
                            {formatResponse(message.content)}
                          </div>
                        </div>
                      ) : (
                        <ChatMessage message={message} />
                      )}
                    </div>
                  ))
                )}
                {isLoading && (
                  <div className="typing-indicator">
                    <div className="typing-dots">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                    <span className="typing-text">Creating personalized plan...</span>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          {/* Input Section (Hidden when form is shown) */}
          {!showStudentForm && (
            <InputField
              value={input}
              onChange={setInput}
              onSubmit={handleSubmit}
              loading={isLoading}
              placeholder={`What would you like to work on for ${studentProfile.name}?`}
            />
          )}
        </div>
      </div>

      <style>{`
        /* Layout fixes */
        .idd-guide-wrapper {
          position: relative;
          overflow: hidden;
          min-height: 100vh;
          width: 100%;
        }

        /* Background sky effects */
        .background-container {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 0;
        }

        .morning-sky {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, #1a6fb5 0%, #4a9fd8 30%, #87ceeb 60%, #c8e6f5 100%);
          animation: skyPulse 10s ease-in-out infinite;
        }

        .morning-sky::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: radial-gradient(circle at 20% 40%, rgba(255,255,255,0.3) 0%, transparent 50%);
          pointer-events: none;
        }

        .night-sky {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, #0a0e2a 0%, #1a1f3a 30%, #0f1832 60%, #0a0e2a 100%);
          animation: nightGlow 8s ease-in-out infinite;
        }

        .night-sky::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: radial-gradient(circle at 80% 20%, rgba(255,255,255,0.15) 0%, transparent 60%);
          pointer-events: none;
        }

        @keyframes skyPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.95; }
        }

        @keyframes nightGlow {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.92; }
        }

        .app-container {
          display: flex;
          min-height: 100vh;
          width: 100%;
          position: relative;
          z-index: 1;
        }

        /* Sidebar styles with glass effect */
        .sidebar {
          background: rgba(0, 0, 0, 0.75);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          width: 280px;
          transition: transform 0.3s ease, width 0.3s ease;
          overflow-y: auto;
          height: 100vh;
          position: sticky;
          top: 0;
          z-index: 100;
          display: flex;
          flex-direction: column;
          border-right: 1px solid rgba(255, 255, 255, 0.1);
        }

        .idd-guide-wrapper.light .sidebar {
          background: rgba(255, 255, 255, 0.85);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border-right: 1px solid rgba(0, 0, 0, 0.1);
        }

        .sidebar.closed {
          width: 70px;
        }

        .sidebar-header {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px 15px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .idd-guide-wrapper.light .sidebar-header {
          border-bottom-color: rgba(0, 0, 0, 0.1);
        }

        .logo {
          font-size: 28px;
        }

        .app-name {
          margin-left: 10px;
          font-size: 18px;
          font-weight: bold;
          color: white;
        }

        .idd-guide-wrapper.light .app-name {
          color: #1a6fb5;
        }

        /* Theme toggle button in sidebar */
        .theme-toggle-sidebar {
          padding: 15px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .idd-guide-wrapper.light .theme-toggle-sidebar {
          border-bottom-color: rgba(0, 0, 0, 0.1);
        }

        .theme-toggle-btn {
          width: 100%;
          padding: 10px;
          border-radius: 8px;
          border: none;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          transition: all 0.3s ease;
          background: rgba(44, 125, 160, 0.3);
          color: white;
          backdrop-filter: blur(5px);
        }

        .idd-guide-wrapper.light .theme-toggle-btn {
          background: rgba(44, 125, 160, 0.2);
          color: #1a6fb5;
        }

        .theme-toggle-btn:hover {
          background: rgba(44, 125, 160, 0.5);
          transform: scale(1.02);
        }

        /* Sidebar Toggle Button - Dynamic positioning */
        .sidebar-toggle {
          position: fixed;
          z-index: 101;
          background: rgba(44, 125, 160, 0.9);
          backdrop-filter: blur(8px);
          border: none;
          color: white;
          font-size: 18px;
          cursor: pointer;
          padding: 10px 14px;
          border-radius: 10px;
          transition: all 0.3s ease;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
        }

        /* When sidebar is open, toggle button is inside the sidebar */
        .sidebar-toggle.sidebar-open {
          left: 240px;
          top: 20px;
          background: rgba(44, 125, 160, 0.8);
        }

        /* When sidebar is closed, toggle button is at top left */
        .sidebar-toggle.sidebar-closed {
          left: 20px;
          top: 20px;
          background: rgba(44, 125, 160, 0.9);
        }

        .sidebar-toggle:hover {
          background: rgba(44, 125, 160, 1);
          transform: scale(1.05);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        }

        /* Main content with glass effect */
        .main-content {
          flex: 1;
          width: calc(100% - 280px);
          transition: width 0.3s ease;
          position: relative;
          display: flex;
          flex-direction: column;
          background: rgba(255, 255, 255, 0.08);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          margin: 10px;
          border-radius: 20px;
          overflow: hidden;
        }

        .idd-guide-wrapper.light .main-content {
          background: rgba(255, 255, 255, 0.35);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
        }

        /* Header styles */
        .chatbot-header {
          background: transparent !important;
          text-align: center;
          padding: 20px 20px 10px 20px;
          position: relative;
        }

        .header-content {
          flex: 1;
          text-align: center;
        }

        .chatbot-title {
          font-size: 28px;
          font-weight: bold;
          margin: 0;
          background: linear-gradient(135deg, #2c7da0, #61a5c2);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          letter-spacing: 2px;
        }

        .idd-guide-wrapper.dark .chatbot-title {
          background: linear-gradient(135deg, #4a9fd8, #87ceeb);
          -webkit-background-clip: text;
          background-clip: text;
        }

        .chatbot-tagline {
          color: #2c7da0;
          font-size: 14px;
          margin-top: 5px;
        }

        .idd-guide-wrapper.dark .chatbot-tagline {
          color: #87ceeb;
        }

        /* Chat messages container */
        .chat-messages-container {
          flex: 1;
          overflow-y: auto;
          padding: 20px;
          min-height: calc(100vh - 200px);
          max-height: calc(100vh - 180px);
          scroll-behavior: smooth;
        }

        /* Dark theme chat backgrounds */
        .idd-guide-wrapper.dark .chat-messages-container {
          background-color: rgba(0, 0, 0, 0.3);
        }

        .idd-guide-wrapper.dark .message-wrapper {
          background-color: transparent;
        }

        .idd-guide-wrapper.dark .formatted-chat-message {
          background-color: rgba(10, 10, 10, 0.7);
        }

        /* Light theme chat backgrounds */
        .idd-guide-wrapper.light .chat-messages-container {
          background-color: rgba(245, 245, 245, 0.3);
        }

        .idd-guide-wrapper.light .message-wrapper {
          background-color: transparent;
        }

        .idd-guide-wrapper.light .formatted-chat-message {
          background-color: rgba(255, 255, 255, 0.7);
        }

        /* Formatted message styles */
        .formatted-chat-message {
          border-radius: 16px;
          padding: 20px;
          margin: 10px 0;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
          backdrop-filter: blur(5px);
          transition: all 0.3s ease;
        }

        .message-role {
          font-weight: bold;
          margin-bottom: 15px;
          font-size: 14px;
          color: #2c7da0;
          border-bottom: 2px solid #2c7da0;
          display: inline-block;
          padding-bottom: 5px;
        }

        /* Section styles */
        .response-section {
          margin-bottom: 30px;
          animation: fadeIn 0.3s ease-in;
        }

        .section-header {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 15px;
          border-bottom: 2px solid rgba(44, 125, 160, 0.3);
          padding-bottom: 8px;
        }

        .section-emoji {
          font-size: 24px;
        }

        .section-title {
          font-size: 18px;
          font-weight: 600;
          color: #2c7da0;
          margin: 0;
        }

        .section-content {
          padding-left: 10px;
        }

        /* Table styles with glass effect */
        .response-table {
          width: 100%;
          border-collapse: collapse;
          margin: 20px 0;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .idd-guide-wrapper.light .response-table {
          background-color: rgba(255, 255, 255, 0.8);
          border: 1px solid rgba(0, 0, 0, 0.1);
        }

        .idd-guide-wrapper.dark .response-table {
          background-color: rgba(30, 30, 30, 0.8);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .response-table thead tr {
          background: linear-gradient(135deg, #2c7da0, #1a6fb5);
          color: white;
        }

        .idd-guide-wrapper.dark .response-table thead tr {
          background: linear-gradient(135deg, #1a5d7a, #0f4a62);
        }

        .response-table th {
          padding: 12px 15px;
          text-align: left;
          font-weight: 600;
          font-size: 14px;
        }

        .response-table td {
          padding: 10px 15px;
          text-align: left;
          border-bottom: 1px solid rgba(0, 0, 0, 0.1);
        }

        .idd-guide-wrapper.dark .response-table td {
          border-bottom-color: rgba(255, 255, 255, 0.1);
        }

        .response-table tbody tr:hover {
          background-color: rgba(44, 125, 160, 0.15);
        }

        /* List styles */
        .response-list {
          margin: 10px 0;
          padding-left: 25px;
          list-style-type: disc;
        }

        .response-list li {
          margin: 5px 0;
          line-height: 1.6;
        }

        .response-paragraph {
          margin: 10px 0;
          line-height: 1.6;
        }

        /* Typing indicator */
        .typing-indicator {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 15px;
          margin: 10px 0;
          border-radius: 12px;
          background-color: rgba(44, 125, 160, 0.15);
          backdrop-filter: blur(5px);
          width: fit-content;
        }

        .typing-dots {
          display: flex;
          gap: 4px;
        }

        .typing-dots span {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background-color: #2c7da0;
          animation: typing 1.4s infinite ease-in-out;
        }

        .typing-dots span:nth-child(1) { animation-delay: -0.32s; }
        .typing-dots span:nth-child(2) { animation-delay: -0.16s; }

        @keyframes typing {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
          30% { transform: translateY(-10px); opacity: 1; }
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        /* Error message */
        .error-message {
          position: fixed;
          top: 20px;
          left: 50%;
          transform: translateX(-50%);
          background: linear-gradient(135deg, #ff4444, #cc0000);
          color: white;
          padding: 12px 24px;
          border-radius: 8px;
          z-index: 1000;
          animation: slideDown 0.3s ease-out;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        @keyframes slideDown {
          from { transform: translateX(-50%) translateY(-100%); opacity: 0; }
          to { transform: translateX(-50%) translateY(0); opacity: 1; }
        }

        /* Student profile form with glass effect */
        .student-profile-form {
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(12px);
          border-radius: 20px;
          padding: 30px;
          margin: 20px;
        }

        .idd-guide-wrapper.light .student-profile-form {
          background: rgba(255, 255, 255, 0.7);
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
        }

        .idd-guide-wrapper.dark .student-profile-form {
          background: rgba(0, 0, 0, 0.5);
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
        }

        /* Mobile responsive */
        @media (max-width: 768px) {
          .sidebar {
            position: fixed;
            transform: translateX(-100%);
            transition: transform 0.3s ease;
            z-index: 1000;
          }
          
          .sidebar.open {
            transform: translateX(0);
          }
          
          .sidebar-toggle.sidebar-open {
            left: auto !important;
            right: 20px;
            top: 20px;
          }
          
          .sidebar-toggle.sidebar-closed {
            left: 20px !important;
            top: 20px;
          }
          
          .main-content {
            width: 100% !important;
            margin: 5px;
          }
          
          .response-table {
            display: block;
            overflow-x: auto;
            white-space: nowrap;
          }
        }
      `}</style>
    </div>
  );
};

export default IDDGuide;